import type { RefObject } from 'react';
import { useRef } from 'react';

import { checkReduceMotion } from '#src/app/pages/petitionDetails/shared/utilities';
import { focus } from '#src/app/shared/utils/dom';

import type { SectionId } from './index';

type IdToRef = Partial<Record<SectionId, RefObject<HTMLDivElement | null>>>;
type Result = ModelHookResult<
	{
		refs: IdToRef;
	},
	{
		scrollToSection: (id: SectionId) => void;
	}
>;

/**
 * Builds the refs for each scrollable petition-details section and a `scrollToSection` action
 * that scrolls to and focuses the section for a given id. The refs object is a Record of Ref
 * objects, where each key is a SectionId and each value is a Ref object containing a reference to
 * the corresponding section element. Will need to add new refs for other sections as needed.
 */
export function useScrollContextValue(): Result {
	const prefersReducedMotion = checkReduceMotion();
	const refs: IdToRef = {
		'decision-makers': useRef<HTMLDivElement | null>(null),
		updates: useRef<HTMLDivElement | null>(null),
		'media-mentions': useRef<HTMLDivElement | null>(null),
		'supporter-voices': useRef<HTMLDivElement | null>(null),
		'endorsements-section': useRef<HTMLDivElement | null>(null),
	};

	const scrollToSection = (id: SectionId) => {
		const ref = refs[id];
		const element = (ref?.current ?? document.getElementById(id)) as HTMLDivElement | null;

		if (!element) return;

		element.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				focus(element, { makeFocusable: true });
			});
		});
	};

	return { data: { refs }, actions: { scrollToSection } };
}
